import React, { useMemo } from 'react';
import styled from 'styled-components';
import { useObserverReturningStore } from '../../../../CodesThatsGottaGoSomewhereElse';
import { CommandDispatcher } from '../../../../concepts/commands/CommandDispatcher';
import { Icon } from '../../../../concepts/resources/Icon';
import { Point } from '../../../../platform/geom/Point';
import { Colors } from '../../../../ui';
import { Fill } from '../../../../ui/Fill';
import { useResolve } from '../../../Container';
import { Flexer } from '../../../dashboard/elements/LayoutElements';
import { AdapterInfo } from '../../AdapterInfo';
import { Camera } from '../../Camera';
import { useEditorSessionObserver } from '../../EditorSession';
import { PointerInfo } from '../../PointerInfo';
import { Selection } from '../../Selection';
import { Physical, RelativeTo, TransformAction, TransformActionOrientation } from '../../entities/Physical';
import { useComponentObserver } from '../../visualization/ComponentObserver';
import { ToolInfo } from '../ToolInfo';

export const TransformLayer = () => {

    const selection = useEditorSessionObserver(Selection);
    const tool = useEditorSessionObserver(ToolInfo);
    const adapter = useEditorSessionObserver(AdapterInfo);
    const object = useMemo(() => selection.firstAndOnly(), [selection.id]);

    if (!object) return null;
    if (tool.state !== 'idle') return null;
    if (adapter.target === object.id) return null;

    return <Fill id="transform-layer" pointerEvents="none" zIndex="1">
        <Flexer pointerEvents="all">
            <RenderActions object={object} />
        </Flexer>
    </Fill>;
};

const RenderAction = ({ object, action }: ActionProps) => <>
    {action.visibility === 'always' ? <RenderAlwaysVisibleAction {...{ object, action }} /> : null}
    {action.visibility === 'proximity' ? <RenderProximityAction {...{ object, action }} /> : null}
</>;

const RenderActions = ({ object }: { object: Physical }) => {
    const state = useComponentObserver(object);
    const actions = useMemo(() => object?.queryTransformActions(true), [object?.id, state]);
    return <>
        {actions.map(x => <RenderAction key={x.key} object={object} action={x} />)}
    </>;
};

type ActionProps = { object: Physical, action: TransformAction };

const ActionMount = styled.div<{ visible: boolean }>`
    position: absolute;
    opacity: ${p => p.visible ? 1 : 0};
    transition: opacity 150ms ease-in-out;
`;

const computeStyling = (camera: Camera, { position: p, orientation: o }: TransformAction) => {

    const transform = (
        (o === 'left' ? 'translate(-100%, -50%) translateX(-20px)' : null) ??
        (o === 'right' ? 'translate(0%, -50%) translateX(20px)' : null) ??
        (o === 'up' ? 'translate(-50%, -100%) translateY(-20px)' : null) ??
        (o === 'down' ? 'translate(-50%, 0%) translateY(20px)' : null) ??
        (o === 'none' ? 'translate(-50%, -50%)' : null) ??
        (o instanceof Point ? `translate(-50%, -50%) translate(${o.format('px')})` : null) ??
        ''
    );

    return {
        ...camera.toView(p).toCSS(),
        transform,
    };
};

const computeAdjustment = (tao: TransformActionOrientation) => {
    return (
        (tao === 'left' ? 'translate(-100%, -50%) translateX(-20px)' : null) ??
        (tao === 'right' ? 'translate(0%, -50%) translateX(20px)' : null) ??
        (tao === 'up' ? 'translate(-50%, -100%) translateY(-20px)' : null) ??
        (tao === 'down' ? 'translate(-50%, 0%) translateY(20px)' : null) ??
        (tao === 'none' ? 'translate(-50%, -50%)' : null) ??
        (tao instanceof Point ? `translate(-50%, -50%) translate(${tao.x}px, ${tao.y}px)` : null) ??
        ''
    );
};

const RenderProximityAction = React.memo<ActionProps>(({ object, action }) => {

    const dispatch = useResolve(CommandDispatcher);
    const camera = useObserverReturningStore(useResolve(Camera));
    const pointer = useEditorSessionObserver(PointerInfo);

    const bounds = camera.toView(object.bounds(RelativeTo.Model));
    const length = Point.vector(bounds[0], bounds[2]).divide(4).length();
    const visible = pointer.position.distanceTo(camera.toView(action.position)) < length;

    const styling = computeStyling(camera, action);

    const onClick = () => {
        dispatch(action.command, [object.id, action.key]);
    };

    return <ActionMount visible={visible} style={styling} onClick={onClick}>
        <RenderStyle {...{ object, action }} />
    </ActionMount>;
});

const RenderAlwaysVisibleAction = React.memo<ActionProps>(({ object, action }) => {

    const dispatch = useResolve(CommandDispatcher);
    const camera = useObserverReturningStore(useResolve(Camera));
    const styling = computeStyling(camera, action);

    const onClick = () => {
        dispatch(action.command, [object.id, action.key]);
    };

    return <ActionMount visible style={styling} onClick={onClick}>
        <RenderStyle {...{ object, action }} />
    </ActionMount>;
});

const RenderStyle = (props: ActionProps) => {
    if (props.action.style.type === 'button') {
        return <RenderButton  {...props} />;
    }
    if (props.action.style.type === 'node') {
        return <RenderNode  {...props} />;
    }
    return null;
};

const ButtonFrame = styled.div`
    padding: 0.125rem 0.325rem;
    font-weight: 450;
    font-size: .875rem;
    border: 2px solid ${Colors.primaryLight};
    border-radius: 4px;
    background: white;
    color: ${Colors.primaryLight};

    &:hover {
        background: ${Colors.primaryLight};
        color: white;
        cursor: pointer;
    }

    &:active {
        background: ${Colors.primary};
        color: white;
        border-color: ${Colors.primary};
    };
`;

const NodeFrame = styled.div`
    width: 20px;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 2px solid ${Colors.primaryLight};
    border-radius: 50%;
    background: white;
    color: ${Colors.primaryLight};

    &:hover {
        background: ${Colors.primaryLight};
        color: white;
        cursor: pointer;
    }

    &:active {
        background: ${Colors.primary};
        color: white;
        border-color: ${Colors.primary};
    };
`;

const RenderButton = React.memo<ActionProps>(({ object, action }) => {
    return <ButtonFrame>
        {action.style.icon && <Icon size="1.25" src={action.style.icon} />}
        {action.style.label && <span style={{ margin: '0 2px' }}>{action.style.label}</span>}
    </ButtonFrame>;
});

const RenderNode = React.memo<ActionProps>(({ object, action }) => {
    return <NodeFrame>
        {action.style.icon && <Icon size="0.75" src={action.style.icon} />}
    </NodeFrame>;
});
